import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import * as R from 'ramda'
import { moment } from '@pbt/pbt-ui-components'

import { PROMPT_TEMPLATES } from '~/components/agent/ingress/promptConfig'
import { AgentIngressPromptType } from '~/components/agent/ingress/types'
import FeatureToggle from '~/constants/featureToggle'
import { getCurrentUserId } from '~/store/reducers/auth'
import { getFeatureToggle } from '~/store/reducers/constants'
import { getPatient } from '~/store/reducers/patients'
import { getUser, getUserName } from '~/store/reducers/users'
import { useCurrentBusinessTimezone } from '~/utils/useCurrentBusinessTimezone'

export interface SuggestedPrompt {
  buttonText: string
  id: string
  prompt: string
}

export interface SuggestedPromptsResult {
  introMessage: string
  prompts: SuggestedPrompt[]
}

export interface PromptContext {
  clientFirstName?: string
  clientId?: string
  clientLastName?: string
  clientName?: string
  hour: number
  isSuggestedMutationPromptsEnabled: boolean
  isSuggestedPromptsEnabled: boolean
  pathname: string
  patientId?: string
  patientName?: string
  userName?: string
}

const getCurrentHour = (timezone: string): number =>
  moment().tz(timezone).hour()

type ConditionFn = (context: PromptContext) => boolean

/**
 * Reusable condition functions for composing prompt logic
 */
const conditions = {
  // Route-based conditions
  isPatientPage: (ctx: PromptContext) =>
    /\/client\/.*\/patient\/.*/.test(ctx.pathname),
  isLandingPage: (ctx: PromptContext) => /\/landing/.test(ctx.pathname),
  isSchedulerPage: (ctx: PromptContext) => /\/scheduler/.test(ctx.pathname),
  isTasksPage: (ctx: PromptContext) => /\/tasks-dashboard/.test(ctx.pathname),

  // Time-based conditions
  isMorning: (ctx: PromptContext) => ctx.hour >= 6 && ctx.hour < 12,
  isAfternoon: (ctx: PromptContext) => ctx.hour >= 12 && ctx.hour < 18,
  isEvening: (ctx: PromptContext) => ctx.hour >= 18 && ctx.hour < 22,
  isBusinessHours: (ctx: PromptContext) => ctx.hour >= 6 && ctx.hour < 18,

  // Data-based conditions
  hasPatientName: (ctx: PromptContext) => Boolean(ctx.patientName),
  hasPatientId: (ctx: PromptContext) => Boolean(ctx.patientId),
  hasUserName: (ctx: PromptContext) => Boolean(ctx.userName),
  hasClientInfo: (ctx: PromptContext) => Boolean(ctx.clientName),

  // Feature toggle conditions
  isSuggestedPromptsEnabled: (ctx: PromptContext) =>
    ctx.isSuggestedPromptsEnabled,
  isSuggestedMutationPromptsEnabled: (ctx: PromptContext) =>
    ctx.isSuggestedMutationPromptsEnabled,
}

const all =
  (...fns: ConditionFn[]): ConditionFn =>
  (ctx) =>
    fns.every((fn) => fn(ctx))

interface PromptConfig {
  buttonText: string | ((ctx: PromptContext) => string)
  condition: ConditionFn
  // Display order (lower = higher priority)
  fallback?: boolean
  id: string
  priority?: number
  prompt: string | ((ctx: PromptContext) => string)
}

interface IntroMessageConfig {
  condition: ConditionFn
  message: string | ((ctx: PromptContext) => string)
}

const createPromptConfigs = (
  t: (key: string, options?: Record<string, unknown>) => string,
): PromptConfig[] => [
  // Patient page prompts
  {
    id: 'patient-medical-history',
    buttonText: (ctx) =>
      t(
        'Agent:SUGGESTED_PROMPTS.BUTTON_TEXT.GENERATE_PATIENT_MEDICAL_HISTORY',
        {
          patientName: ctx.patientName,
        },
      ),
    prompt: (ctx) =>
      PROMPT_TEMPLATES[AgentIngressPromptType.MEDICAL_HISTORY].buildPrompt({
        patientName: ctx.patientName,
        patientId: ctx.patientId,
      }),
    condition: all(
      conditions.isPatientPage,
      conditions.hasPatientId,
      conditions.hasPatientName,
    ),
    priority: 2,
  },

  // Landing page prompts
  {
    id: 'todays-appointments',
    buttonText: t('Agent:SUGGESTED_PROMPTS.BUTTON_TEXT.TODAYS_APPOINTMENTS'),
    prompt: "Show me a summary of today's appointments",
    condition: all(
      conditions.isLandingPage,
      conditions.isBusinessHours,
      conditions.isSuggestedPromptsEnabled,
    ),
    priority: 1,
  },
  {
    id: 'tomorrows-appointments',
    buttonText: t('Agent:SUGGESTED_PROMPTS.BUTTON_TEXT.TOMORROWS_APPOINTMENTS'),
    prompt: "Show me a summary of tomorrow's appointments",
    condition: all(
      conditions.isLandingPage,
      conditions.isBusinessHours,
      conditions.isSuggestedPromptsEnabled,
    ),
    priority: 2,
  },

  // Scheduler page prompts
  {
    id: 'schedule-appointment',
    buttonText: t('Agent:SUGGESTED_PROMPTS.BUTTON_TEXT.SCHEDULE_APPOINTMENT'),
    prompt: 'Can you help me schedule an appointment?',
    condition: all(
      conditions.isSchedulerPage,
      conditions.isSuggestedPromptsEnabled,
      conditions.isSuggestedMutationPromptsEnabled,
    ),
    priority: 1,
  },

  // Tasks page prompts
  {
    id: 'my-tasks-summary',
    buttonText: t('Agent:SUGGESTED_PROMPTS.BUTTON_TEXT.SUMMARIZE_MY_TASKS'),
    prompt: (ctx) =>
      `Show me a summary of the tasks assigned to me, ${ctx.userName}`,
    condition: all(
      conditions.isTasksPage,
      conditions.hasUserName,
      conditions.isSuggestedPromptsEnabled,
    ),
    priority: 1,
  },

  // Default fallback prompt (only shows if no other prompts match)
  {
    id: 'what-can-you-do',
    buttonText: t('Agent:SUGGESTED_PROMPTS.BUTTON_TEXT.WHAT_CAN_YOU_DO'),
    prompt: 'What can you help me with?',
    condition: R.T, // Always available as fallback
    priority: 999,
    fallback: true,
  },
]

const createIntroMessages = (
  t: (key: string) => string,
): IntroMessageConfig[] => [
  // Patient page
  {
    condition: conditions.isPatientPage,
    message: t('Agent:SUGGESTED_PROMPTS.INTRO_MESSAGES.PATIENT_PAGE'),
  },

  {
    condition: all(
      conditions.isSchedulerPage,
      conditions.isSuggestedPromptsEnabled,
    ),
    message: t('Agent:SUGGESTED_PROMPTS.INTRO_MESSAGES.SCHEDULER_PAGE'),
  },

  // General time-based fallbacks
  {
    condition: all(conditions.isMorning, conditions.isSuggestedPromptsEnabled),
    message: t('Agent:SUGGESTED_PROMPTS.INTRO_MESSAGES.GOOD_MORNING'),
  },
  {
    condition: all(
      conditions.isAfternoon,
      conditions.isSuggestedPromptsEnabled,
    ),
    message: t('Agent:SUGGESTED_PROMPTS.INTRO_MESSAGES.GOOD_AFTERNOON'),
  },
  {
    condition: all(conditions.isEvening, conditions.isSuggestedPromptsEnabled),
    message: t('Agent:SUGGESTED_PROMPTS.INTRO_MESSAGES.GOOD_EVENING'),
  },

  // Final fallback (always matches)
  {
    condition: R.T,
    message: t('Agent:SUGGESTED_PROMPTS.INTRO_MESSAGES.DEFAULT'),
  },
]

/**
 * Extract clientId and patientId from pathname
 * Handles routes like: /client/:clientId/patient/:patientId
 */
const extractIdsFromPathname = (
  pathname: string,
): { clientId?: string; patientId?: string } => {
  const clientMatch = pathname.match(/\/client\/([A-Za-z0-9-]+)/)
  const patientMatch = pathname.match(/\/patient\/([A-Za-z0-9-]+)/)

  return {
    clientId: clientMatch?.[1],
    patientId: patientMatch?.[1],
  }
}

/**
 * Custom hook that returns suggested prompts based on:
 * - Current page/route
 * - Time of day (in business timezone)
 * - Redux state (patient, client data, etc.)
 * - Other contextual factors (extensible)
 */
export const useAgentSuggestedPrompts = (): SuggestedPromptsResult => {
  const location = useLocation()
  const businessTimezone = useCurrentBusinessTimezone()
  const hour = getCurrentHour(businessTimezone)
  const { t } = useTranslation()

  // Extract IDs from pathname since we are outside the Router context
  const { clientId, patientId } = extractIdsFromPathname(location.pathname)

  const patient = useSelector(getPatient(patientId))
  const client = useSelector(getUser(clientId))
  const currentUserId = useSelector(getCurrentUserId)
  const userName = useSelector(getUserName(currentUserId))
  const isSuggestedPromptsEnabled = useSelector(
    getFeatureToggle(FeatureToggle.AGENT_CHAT_SUGGESTED_PROMPTS),
  )
  const isSuggestedMutationPromptsEnabled = useSelector(
    getFeatureToggle(FeatureToggle.AGENT_CHAT_SUGGESTED_MUTATION_PROMPTS),
  )

  const promptConfigs = useMemo(() => createPromptConfigs(t), [t])
  const introMessages = useMemo(() => createIntroMessages(t), [t])

  return useMemo(() => {
    const context: PromptContext = {
      hour,
      pathname: location.pathname,
      patientId,
      clientId,
      patientName: patient?.name,
      clientFirstName: client?.firstName,
      clientLastName: client?.lastName,
      clientName: client
        ? `${client.firstName || ''} ${client.lastName || ''}`.trim()
        : undefined,
      userName,
      isSuggestedPromptsEnabled:
        typeof isSuggestedPromptsEnabled === 'boolean' &&
        isSuggestedPromptsEnabled,
      isSuggestedMutationPromptsEnabled:
        typeof isSuggestedMutationPromptsEnabled === 'boolean' &&
        isSuggestedMutationPromptsEnabled,
    }

    const introConfig = introMessages.find((config) =>
      config.condition(context),
    )
    const introMessage = introConfig?.message

    const resolvedIntro =
      typeof introMessage === 'function'
        ? introMessage(context)
        : (introMessage ?? 'How can I help you today?')

    const allMatchingPrompts = promptConfigs
      .filter((config) => config.condition(context))
      .sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0))

    const hasNonFallbackPrompts = allMatchingPrompts.some((p) => !p.fallback)
    const filteredPrompts = hasNonFallbackPrompts
      ? allMatchingPrompts.filter((p) => !p.fallback)
      : allMatchingPrompts

    const prompts = filteredPrompts.map((config) => ({
      id: config.id,
      buttonText:
        typeof config.buttonText === 'function'
          ? config.buttonText(context)
          : config.buttonText,
      prompt:
        typeof config.prompt === 'function'
          ? config.prompt(context)
          : config.prompt,
    }))

    return {
      introMessage: resolvedIntro,
      prompts,
    }
  }, [
    location.pathname,
    businessTimezone,
    hour,
    patientId,
    clientId,
    patient?.name,
    client?.firstName,
    client?.lastName,
    userName,
    isSuggestedPromptsEnabled,
    isSuggestedMutationPromptsEnabled,
    promptConfigs,
    introMessages,
  ])
}

export default useAgentSuggestedPrompts
